import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { PageHeader, StageBadge } from "@/components/app/UI";
import { Calendar } from "@/components/ui/calendar";
import { Phone, ChevronRight } from "lucide-react";

const sameDay = (a, b) => a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Date());

  useEffect(() => {
    api.get("/follow-ups/all").then((r) => setItems(r.data));
  }, []);

  const parsed = useMemo(
    () => items.map((i) => ({ ...i, _date: i.due_at ? new Date(i.due_at) : null })),
    [items]
  );

  const dueDays = useMemo(() => parsed.filter((i) => i._date).map((i) => i._date), [parsed]);
  const forSelected = parsed.filter((i) => sameDay(i._date, selected));
  const upcoming = parsed
    .filter((i) => i._date && i._date >= new Date(new Date().setHours(0, 0, 0, 0)) && !i.done)
    .slice(0, 8);

  return (
    <div data-testid="calendar-page">
      <PageHeader
        eyebrow="CRM"
        title="Follow-up Calendar"
        subtitle="All scheduled follow-ups across every lead. Click a date to see the list, click a lead to jump in."
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-8">
        <div className="lg:col-span-2 flat-card p-6">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            modifiers={{ due: dueDays }}
            modifiersClassNames={{ due: "bg-[#002FA7] !text-white font-bold" }}
            className="rounded-none"
            data-testid="follow-up-calendar"
          />
          <div className="mt-6 border-t border-[#E5E7EB] pt-4">
            <div className="label-eyebrow mb-3">
              {selected.toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
              {" · "}{forSelected.length} follow-up{forSelected.length === 1 ? "" : "s"}
            </div>
            <div className="space-y-2">
              {forSelected.length === 0 && (
                <div className="text-xs text-[#9CA3AF] italic py-4">Nothing scheduled for this date.</div>
              )}
              {forSelected.map((f) => (
                <div
                  key={f.id}
                  className="border border-[#E5E7EB] hover:border-[#0a0a0a] p-3 flex items-center justify-between gap-3"
                  data-testid={`followup-${f.id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!f.done}
                    onChange={async (e) => {
                      if (f.inline) {
                        // Inline entries live on the lead's next_followup_date field —
                        // mark done = clear that field. Re-add via "Schedule Follow-up" if needed.
                        await api.patch(`/leads/${f.lead_id}`, { next_followup_date: null });
                      } else {
                        await api.patch(`/leads/${f.lead_id}/follow-ups/${f.id}`, { done: e.target.checked });
                      }
                      api.get("/follow-ups/all").then((r) => setItems(r.data));
                    }}
                    className="w-4 h-4 accent-[#002FA7]"
                    data-testid={`followup-done-${f.id}`}
                  />
                  <Link to={`/leads/${f.lead_id}`} className="flex-1 min-w-0">
                    <div className={`text-sm font-bold tracking-tight truncate ${f.done ? "line-through text-[#9CA3AF]" : ""}`}>
                      {f.lead_name}
                      {f.inline && <span className="ml-2 text-[9px] uppercase tracking-widest font-bold bg-[#EFF6FF] text-[#002FA7] px-1.5 py-0.5">Scheduled</span>}
                    </div>
                    <div className="text-xs text-[#4B5563] truncate">{f.note}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#4B5563] mt-1 flex items-center gap-2">
                      <Phone className="w-3 h-3" /> {f.lead_phone} <StageBadge stage={f.stage} />
                    </div>
                  </Link>
                  <ChevronRight className="w-4 h-4 text-[#4B5563]" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flat-card p-6">
          <div className="label-eyebrow mb-3">Upcoming · next {upcoming.length}</div>
          <div className="space-y-2">
            {upcoming.map((f) => (
              <Link
                key={f.id}
                to={`/leads/${f.lead_id}`}
                className="block border border-[#E5E7EB] hover:border-[#0a0a0a] p-3"
                data-testid={`upcoming-${f.id}`}
              >
                <div className="text-xs label-eyebrow mb-1">{fmtDate(f.due_at)}</div>
                <div className="text-sm font-bold tracking-tight truncate">{f.lead_name}</div>
                <div className="text-xs text-[#4B5563] truncate">{f.note}</div>
              </Link>
            ))}
            {upcoming.length === 0 && <div className="text-xs text-[#9CA3AF] italic py-4">No upcoming follow-ups.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
